import { useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import Icon from "../components/Icon";
import { fullPageLoad } from "../navigate";
import ShareBackMe from "../components/ShareBackMe";
import { contestURL, normalizeViewer, postJSON, reasonOf, type ContestDetail, type ContestViewer, type Market } from "./api";
import { Alert, primaryButton, secondaryButton } from "./ui";
import { fileOK, putFile } from "./upload";

type Props = {
  contest: ContestDetail;
  onViewer: (viewer: ContestViewer) => void;
  market?: Market | null;
  username?: string;
};

// Home Turf has no house money, so an entrant's own backers are the whole
// pot. Said plainly, with the share one tap away.
const HOME_TURF_HEADING = "No house bonus here. You bring the backers.";
const HOME_TURF_SUB = "Nothing from the house goes into this pot. Every fan who picks you grows it.";

function Panel({ testId, heading, children }: { testId: string; heading: string; children?: ReactNode }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
      <h3 className="font-headline text-2xl uppercase leading-none text-chrome">{heading}</h3>
      {children}
    </section>
  );
}

const isHttps = (url: string) => {
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
};

// The Join tab shows exactly one thing the viewer can do, from the state and
// viewer block the server sent. The server refuses anything it shouldn't.
export default function JoinTab({ contest, onViewer, market, username }: Props) {
  const j = copy.contest.join;
  const { viewer, state, slug } = contest;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!viewer.logged_in) {
    return (
      <Panel testId="join-signin" heading={j.signIn}>
        <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.signInSub}</p>
        <Link to={`/login?next=${encodeURIComponent(`/contest/${slug}`)}`} className={`${primaryButton} mt-5`}>
          {j.signIn}
        </Link>
      </Panel>
    );
  }
  if (!viewer.email_verified) {
    return (
      <Panel testId="join-verify" heading={j.verifyHeading}>
        <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.verifySub}</p>
        <a href="/send-verification" className={`${secondaryButton} mt-5`}>
          <Icon name="mail" className="h-4 w-4" />
          {j.verify}
        </a>
      </Panel>
    );
  }
  if (state !== "open") {
    return (
      <Panel testId="join-closed" heading={j.closedHeading}>
        <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.closedSub}</p>
      </Panel>
    );
  }
  if (viewer.is_sponsor) {
    return (
      <Panel testId="join-sponsor" heading={j.sponsorHeading}>
        <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.sponsorSub}</p>
      </Panel>
    );
  }

  const run = async (url: string, body: unknown) => {
    setBusy(true);
    setError(null);
    const reply = await postJSON(url, body);
    setBusy(false);
    if (!reply.ok) setError(reasonOf(reply) ?? j.failed);
    return reply;
  };

  const reserve = async () => {
    const reply = await run(contestURL(slug, "/reserve"), {});
    if (reply.ok) onViewer(normalizeViewer(reply.body.viewer));
  };

  const payEntry = async (paypal: boolean) => {
    const reply = await run(contestURL(slug, "/entry"), { paypal });
    if (!reply.ok) return;
    const url = typeof reply.body.payment_url === "string" ? reply.body.payment_url : "";
    if (url) {
      if (isHttps(url)) fullPageLoad(url);
      else setError(j.failed);
      return;
    }
    if (reply.body.joined === true) onViewer(normalizeViewer(reply.body.viewer));
  };

  const body = (() => {
    if (viewer.can_submit) {
      return <SubmitForm slug={slug} onViewer={onViewer} onDone={() => setNotice(j.submitted)} />;
    }
    if (viewer.has_submitted) {
      return (
        <Panel testId="join-submitted" heading={j.submittedHeading}>
          <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.submittedSub}</p>
        </Panel>
      );
    }
    const fee = contest.admission.kind === "fee" ? contest.admission.display : undefined;
    if (viewer.can_reserve || (viewer.has_reservation && fee)) {
      return fee ? (
        <PaidEntry fee={fee} busy={busy} onPay={payEntry} />
      ) : (
        <Panel testId="join-free" heading={j.freeHeading}>
          <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.freeSub}</p>
          <button type="button" onClick={reserve} disabled={busy} className={`${primaryButton} mt-5`}>
            {busy ? j.working : j.joinFree}
          </button>
        </Panel>
      );
    }
    return (
      <Panel testId="join-full" heading={j.fullHeading}>
        <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.fullSub}</p>
      </Panel>
    );
  })();

  const homeTurf = market?.status === "open" && market.tier === "home_turf" && viewer.is_entrant && !!username;

  return (
    <div className="space-y-5">
      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert>{error}</Alert>}
      {body}
      {homeTurf && username && (
        <Panel testId="join-home-turf" heading={HOME_TURF_HEADING}>
          <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{HOME_TURF_SUB}</p>
          <div className="mt-4">
            <ShareBackMe slug={slug} contest={contest.title} username={username} />
          </div>
        </Panel>
      )}
      {viewer.has_reservation && <Cosign slug={slug} />}
    </div>
  );
}

function PaidEntry({ fee, busy, onPay }: { fee: string; busy: boolean; onPay: (paypal: boolean) => void }) {
  const j = copy.contest.join;
  const [paypal, setPaypal] = useState(false);
  return (
    <Panel testId="join-paid" heading={j.paidHeading}>
      <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.paidSub(fee)}</p>
      <label className="mt-4 flex cursor-pointer items-center gap-2.5 text-[12px] text-chrome">
        <input
          type="checkbox"
          checked={paypal}
          onChange={(e) => setPaypal(e.target.checked)}
          className="bf-focusable h-4 w-4 cursor-pointer accent-money"
        />
        {j.paypal}
      </label>
      <button type="button" onClick={() => onPay(paypal)} disabled={busy} className={`${primaryButton} mt-5`}>
        {busy ? j.working : j.payEntry(fee)}
      </button>
    </Panel>
  );
}

function SubmitForm({ slug, onViewer, onDone }: { slug: string; onViewer: (v: ContestViewer) => void; onDone: () => void }) {
  const j = copy.contest.join;
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const busy = progress !== null;

  const choose = (f: File | undefined) => {
    setError(null);
    if (!f) return setFile(null);
    if (!fileOK(f)) {
      setFile(null);
      if (input.current) input.current.value = "";
      setError(j.badFile);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file || busy) return;
    setError(null);
    setProgress(0);
    const fail = (reason?: string) => {
      setProgress(null);
      setError(reason ?? j.failed);
    };
    const presign = await postJSON(contestURL(slug, "/submission/presign"), { filename: file.name, size: file.size });
    const b = presign.body;
    if (!presign.ok || typeof b.upload_url !== "string" || typeof b.submission_id !== "string" || typeof b.key !== "string") {
      return fail(reasonOf(presign));
    }
    const put = await putFile(b.upload_url, file, typeof b.content_type === "string" ? b.content_type : "", setProgress);
    if (!put) return fail();
    const done = await postJSON(contestURL(slug, "/submission"), { submission_id: b.submission_id, key: b.key });
    if (!done.ok) return fail(reasonOf(done));
    setProgress(null);
    onDone();
    onViewer(normalizeViewer(done.body.viewer));
  };

  return (
    <Panel testId="join-submit" heading={j.submitHeading}>
      <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.submitSub}</p>
      <label className="mt-4 block">
        <span className="sr-only">{j.chooseFile}</span>
        <input
          ref={input}
          data-testid="submit-file"
          type="file"
          accept=".mp3,.wav,audio/mpeg,audio/wav"
          disabled={busy}
          onChange={(e) => choose(e.target.files?.[0])}
          className="block w-full text-[12px] text-chrome-dim file:mr-3 file:h-10 file:cursor-pointer file:rounded-md file:border file:border-ground-edge file:bg-ground file:px-4 file:text-[11px] file:font-bold file:uppercase file:tracking-[0.14em] file:text-chrome"
        />
      </label>
      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}
      <button type="button" onClick={submit} disabled={!file || busy} className={`${primaryButton} mt-5`}>
        <Icon name="upload" className="mr-2 h-4 w-4" />
        {busy ? j.uploading(progress ?? 0) : j.submit}
      </button>
    </Panel>
  );
}

function Cosign({ slug }: { slug: string }) {
  const j = copy.contest.join;
  const [state, setState] = useState<{ copied?: boolean; manual?: string; error?: string }>({});
  const [busy, setBusy] = useState(false);

  const get = async () => {
    setBusy(true);
    const reply = await postJSON(contestURL(slug, "/cosign"), {});
    setBusy(false);
    const link = typeof reply.body.link === "string" ? reply.body.link : "";
    if (!reply.ok || !link) return setState({ error: reasonOf(reply) ?? j.failed });
    try {
      await navigator.clipboard.writeText(link);
      setState({ copied: true });
    } catch {
      setState({ manual: link });
    }
  };

  return (
    <Panel testId="join-cosign" heading={j.cosignHeading}>
      <p className="mt-3 text-[12px] leading-relaxed text-chrome-dim">{j.cosignSub}</p>
      <button type="button" onClick={get} disabled={busy} className={`${secondaryButton} mt-4`}>
        <Icon name={state.copied ? "check" : "fileCopy"} className="h-4 w-4" />
        {state.copied ? j.cosignCopied : j.cosign}
      </button>
      {state.manual && (
        <p className="mt-3 break-all text-[12px] text-chrome">
          {j.cosignManual} <span data-testid="cosign-link">{state.manual}</span>
        </p>
      )}
      {state.error && (
        <div className="mt-3">
          <Alert>{state.error}</Alert>
        </div>
      )}
    </Panel>
  );
}
